import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "linii-documente-wrapper" }
const _hoisted_2 = { style: {"display":"inline-flex","align-items":"center","gap":"4px"} }
const _hoisted_3 = { key: 0 }

import { ref, shallowRef, computed, watch, onMounted, onUnmounted } from 'vue';
import DxDataGrid, {
    DxColumn,
    DxEditing,
    DxExport,
    DxItem,
    DxLookup,
    DxRangeRule,
    DxRequiredRule,
    DxSelection,
    DxStateStoring,
    DxToolbar,
} from 'devextreme-vue/data-grid';
import DxDropDownBox from 'devextreme-vue/drop-down-box';
import DxTreeView from 'devextreme-vue/tree-view';
import DxButton from 'devextreme-vue/button';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import notify from 'devextreme/ui/notify';
import AppSetting from '@/settings/appSettings';
import http from '@/settings/apiSettings';
import DOC_Linii from '@/services/Documente/DOC_Linii';
import PopupImportLiniiExcel from './PopupImportLiniiExcel.vue';

interface DocumentLiniiProp {
    Id?: string;
    Categorie?: string | null;
    Curs?: number | null;
    TipId?: string | null;
    [key: string]: unknown;
}

interface TipDocumentInfo {
    CrossCateg?: number | null;
    AreIntrare?: boolean | null;
    AreIesire?: boolean | null;
}
const EDITOR_REVERT_BUTTON_NAME = 'revertField';


export default /*@__PURE__*/_defineComponent({
  __name: 'GridDocumentLinii',
  props: {
    document: {}
  },
  emits: ["refresh-document", "has-linii-changed", "dirty-count-change", "open-etichete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;

// ── Gating Etichete — vizibil doar când tipul documentului e NIVEL_GESTIUNE(0)/NIVEL_MF(2)
// și are cel puțin intrare sau ieșire (regulă portată din legacy DOC_DocumentForm.cs, vezi
// PLAN_IMPLEMENTARE_ETICHETE.md Faza 3). Pattern de fetch identic cu LiniView.vue (onTiparirePDF).
const tipDocumentInfo = ref<TipDocumentInfo | null>(null);

async function fetchTipDocumentInfo() {
    tipDocumentInfo.value = null;
    if (!props.document?.TipId) return;
    try {
        const resp = await http.clientDocumenteOdata.get(
            `DOC_Tipuri(${props.document.TipId})?$select=CrossCateg,AreIntrare,AreIesire`
        );
        tipDocumentInfo.value = resp.data;
    } catch {
        /* ignore — gating rămâne dezactivat dacă tipul nu poate fi citit */
    }
}

const areEtichete = computed(() => {
    const info = tipDocumentInfo.value;
    if (!info) return false;
    return [0, 2].includes(info.CrossCateg ?? -1) && !!(info.AreIntrare || info.AreIesire);
});

watch(() => props.document?.TipId, fetchTipDocumentInfo);

function onOpenEticheteLinie(rowData: any) {
    emit('open-etichete', { linieId: rowData?.Id ?? null });
}

function onOpenEticheteDocument() {
    emit('open-etichete', { linieId: null });
}

// ── Vizibilitate coloane per categorie ──
// Listă de coloane VIZIBILE implicit pentru fiecare categorie de document — restul coloanelor
// rămân declarate o singură dată în grid, doar ascunse implicit (accesibile din Column Chooser).
// Portat 1:1 din vechile componente linii-tabele/*.vue (research verificat pe fiecare fișier sursă).
const CATEGORII_COLOANE_VIZIBILE: Record<string, string[]> = {
    'Contabilitate': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Total'],
    'Facturi furnizor': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Tva', 'Total'],
    'Facturi client': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Tva', 'Total'],
    'Mijloace fixe': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Total'],
    'Personal - Salarizare': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare', 'Total'],
    'Documente gestiune': ['Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'Valoare'],
    'Solduri Initiale': [
        'Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'PretUnitar', 'Valoare', 'AdaosUnitar', 'Total',
        'LocDepozitare', 'CategorieContabila', 'UnitateAmbalaj', 'CantitateFa', 'CantitatePt1Fa',
        'CodLotFurnizor', 'DataExp', 'CodLotIntern', 'TaxaUnitara',
    ],
    'Documente productie': [
        'Cod', 'CodIntern', 'Nume', 'Cantitate', 'Unitate', 'PretUnitar', 'Valoare', 'AdaosUnitar', 'Total',
        'LocDepozitare', 'CategorieContabila', 'UnitateAmbalaj', 'CantitateFa', 'CantitatePt1Fa',
        'CodLotFurnizor', 'DataExp', 'CodLotIntern', 'TaxaUnitara',
    ],
};

// Coloane care rămân condiționate de Categorie (LocDepozitare, CodLotFurnizor etc.), mai
// coloanele specifice etichetelor (Etichete/Cant. pt. 1 FA/UM FA) devin vizibile suplimentar
// pe orice document unde `areEtichete===true`, indiferent de textul Categorie — regula reală e
// pe CrossCateg/AreIntrare/AreIesire ale tipului, nu pe eticheta text (vezi PLAN_IMPLEMENTARE_ETICHETE.md).
const COLOANE_ETICHETE = ['CantitateFa', 'CantitatePt1Fa', 'UnitateAmbalaj'];

function colVisible(key: string): boolean {
    const categorie = props.document?.Categorie ?? '';
    const visibleByCategorie = CATEGORII_COLOANE_VIZIBILE[categorie]?.includes(key) ?? false;
    if (visibleByCategorie) return true;
    if (areEtichete.value && COLOANE_ETICHETE.includes(key)) return true;
    return false;
}

const stateStoringKey = computed(() => {
    const slug = (props.document?.Categorie || 'default').toString().replace(/\s+/g, '-').toLowerCase();
    return `grid-document-linii-${slug}`;
});

// ── Grid & date ──
const gridRef = ref<InstanceType<typeof DxDataGrid> | null>(null);
const dsLinii = shallowRef<DataSource | null>(null);
const dsProdus = shallowRef<DataSource | null>(null);
const dsUnitate = shallowRef<DataSource | null>(null);
const dsCategorieContabila = shallowRef<DataSource | null>(null);
const dsConturi = shallowRef<DataSource | null>(null);
const dsCustomList = shallowRef<DataSource | null>(null);
const dsStructuraCompanie = shallowRef<DataSource | null>(null);
const structuraCompanieItems = shallowRef<any[]>([]);
const structuraCompanieTree = shallowRef<any[]>([]);
const isSalvandTot = ref(false);

// ── Blocare/deblocare grid — implicit blocat (read-only), niciun buton de editare
// (Adaugă linie/Import Excel/Salvează tot/Etichete) nu apare până nu se deblochează explicit;
// se reblochează automat la schimbarea documentului afișat, ca să nu rămână "deschis" din greșeală.
const isUnlocked = ref(false);
const showImportExcelPopup = ref(false);

function onToggleLock() {
    isUnlocked.value = !isUnlocked.value;
    if (!isUnlocked.value) gridRef.value?.instance?.cancelEditData();
}

watch(() => props.document?.Id, () => { isUnlocked.value = false; });

function authHeader(e: any) {
    e.headers = {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
    };
}

function toast(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', displayTime = 3000) {
    notify(message, type, displayTime);
}

function getErrMsg(err: any): string {
    const data = err?.response?.data;
    if (data) {
        if (typeof data === 'string') return data;
        if (data.error?.message) return data.error.message;
        if (data.message) return data.message;
        if (data.title) return data.title;
    }
    return err?.message ?? 'A apărut o eroare.';
}

function loadDataSources() {
    dsLinii.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'DOC_Linii',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            fieldTypes: { DocumentId: 'Guid', StructIdEditabil: 'Guid' },
            beforeSend: (e: any) => {
                authHeader(e);
                e.params.$expand = [
                    'Produs($select=Cod,CodIntern,Nume,UnitateId,CategorieContabilaId)',
                    'Unitate($select=Simbol)',
                    'CategorieContabila($select=Id,Nume)',
                    'Debit($select=Id,Simbol,Nume)',
                    'Credit($select=Id,Simbol,Nume)',
                    'CustomList($select=Id,Nume)',
                    'StructuraCompanie($select=Id,Descr,Nume)',
                    'UnitateAmbalaj($select=Simbol)',
                ].join(',');
            },
        }),
    });
    dsLinii.value.filter(['DocumentId', '=', props.document.Id]);
    dsLinii.value.load().then((data: any[]) => {
        emit('has-linii-changed', (data?.length ?? 0) > 0);
    });

    dsProdus.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'PRO_Produse',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
        select: ['Id', 'Cod', 'CodIntern', 'Nume', 'UnitateId', 'CategorieContabilaId'],
        paginate: true,
        pageSize: 25,
    });

    dsUnitate.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_ComercialOdata + 'PRO_Unitati',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });

    dsCategorieContabila.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'CON_CategoriiContabile',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });

    dsConturi.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'CON_Conturi',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });

    dsCustomList.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'DOC_CustomList',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });

    dsStructuraCompanie.value = new DataSource({
        paginate: false,
        store: new ODataStore({
            url: AppSetting.API_Url_DocumenteOdata + 'ERP_StructuraCompanie',
            key: 'Id',
            keyType: 'Guid',
            version: 4,
            beforeSend: authHeader,
        }),
    });
    dsStructuraCompanie.value.load().then((items: any[]) => {
        structuraCompanieItems.value = items;
        structuraCompanieTree.value = buildDepartamentTree(items);
    });
}

function buildDepartamentTree(flat: any[], parentId: string | null = null): any[] {
    const pid = parentId != null ? String(parentId).toLowerCase() : null;
    return flat
        .filter((i) => (i.ParentId != null ? String(i.ParentId).toLowerCase() : null) === pid)
        .map((i) => ({ ...i, expanded: true, items: buildDepartamentTree(flat, i.Id) }));
}

function departamentDisplayExpr(item: any): string {
    if (!item) return '';
    return item.Descr ? `${item.Descr} - ${item.Nume || ''}` : item.Nume || '';
}

function getDepartamentDisplay(rowData: any): string {
    return departamentDisplayExpr(rowData?.StructuraCompanie);
}

function contDisplayExpr(item: any): string {
    if (!item) return '';
    return `${item.Simbol || ''} — ${item.Nume || ''}`;
}

function produsDisplayExpr(item: any): string {
    if (!item) return '';
    return `${item.Cod || ''} - ${item.Nume || ''} - ${item.CodIntern || ''}`;
}

function getProdusNume(rowData: any): string {
    return rowData?.Produs?.Nume ?? '';
}

function calculateTotalRon(rowData: any): number | null {
    if (rowData.Total == null || !props.document?.Curs) return null;
    return rowData.Total * (props.document.Curs as number);
}

// ── Dropdown produs: navigare tastatură + auto-completare UM/Categorie Contabilă ──
let currentDropBoxInstance: any = null;
let shouldFocusFilter = false;
const innerProdusGridRef = ref<InstanceType<typeof DxDataGrid> | null>(null);

function onDropBoxOpened(e: any) {
    currentDropBoxInstance = e.component;
    shouldFocusFilter = true;
}

function onDropBoxClosed() {
    // currentDropBoxInstance rămâne — mai e nevoie de el pentru option('value')/close() ulterior
}

function onProdusBoxKeyDown(e: any, _cellInfo: any) {
    if (e.event.key === 'Enter') {
        e.event.stopPropagation();
        e.event.preventDefault();
        e.component.open();
    }
}

function onProdusGridContentReady() {
    if (!shouldFocusFilter) return;
    shouldFocusFilter = false;
    window.setTimeout(() => {
        const el = (innerProdusGridRef.value as any)?.$el as HTMLElement | undefined;
        const input = el?.querySelector<HTMLInputElement>('.dx-datagrid-filter-row .dx-texteditor-input');
        input?.focus();
    }, 0);
}

function onProdusGridKeyDown(e: any, cellInfo: any) {
    if (e.event.key !== 'Enter') return;
    e.event.stopPropagation();
    if (e.event.target?.closest?.('.dx-datagrid-filter-row')) return;
    e.event.preventDefault();
    const grid = e.component;
    const focusedRowIndex = grid.option('focusedRowIndex');
    if (focusedRowIndex < 0) return;
    const rowData = grid.getVisibleRows()[focusedRowIndex]?.data;
    if (rowData) onProdusGridSelection({ selectedRowsData: [rowData] }, cellInfo);
}

function onProdusGridSelection(e: any, cellInfo: any) {
    const prod = e.selectedRowsData[0];
    if (!prod) return;
    cellInfo.data.Produs = { Cod: prod.Cod, CodIntern: prod.CodIntern, Nume: prod.Nume };
    if (prod.UnitateId != null) cellInfo.data.UnitateId = prod.UnitateId;
    if (prod.CategorieContabilaId != null) cellInfo.data.CategorieContabilaId = prod.CategorieContabilaId;
    cellInfo.setValue(prod.Id);
    currentDropBoxInstance?.option('value', prod.Id);
    currentDropBoxInstance?.close();
    window.setTimeout(() => {
        gridRef.value?.instance?.editCell(cellInfo.rowIndex, 'Cantitate');
    }, 100);
}

// ── Salvare (adăugare/editare inline, batch) ──
// Payload generic — toate câmpurile posibile ale DOC_CreareLinii, indiferent de categorie.
// Backend-ul (DOC_LiniiRepository.AddLinie/UpdateLinie) ignoră câmpurile irelevante pentru
// tipul de document curent — nu e nevoie de ramificare pe Categorie ca în vechile componente.
// Decizie: NU trimitem niciodată ValoareLinie/ValoareTvaLinie explicit — backend-ul le
// calculează mereu corect din Cantitate/PretUnitar/AdaosLinie, sursa de adevăr rămânând
// întotdeauna backend-ul (confirmat după reload), conform ghidului acestei sarcini.
function buildLiniePayload(row: any) {
    return {
        ProdusId: row.ProdusId ?? null,
        UnitateId: row.UnitateId ?? null,
        // Fallback pe 1, nu 0 — o linie cu Cantitate=0 are mereu Valoare=0, indiferent de preț
        // (vezi onInitNewRow mai sus, care ar trebui să prevină deja acest caz).
        Cantitate: row.Cantitate || 1,
        PretUnitar: row.PretUnitar ?? null,
        TvaLinie: row.Tva ?? null,
        CategorieContabilaId: row.CategorieContabilaId ?? null,
        DebitId: row.DebitId ?? null,
        CreditId: row.CreditId ?? null,
        AdaosLinie: row.Adaos ?? 0,
        AdaosUnitarLinie: row.AdaosUnitar ?? 0,
        ValoareAdaosLinie: row.ValoareAdaos ?? 0,
        ProdusNume: row.Nume ?? null,
        CategTva: row.CategTva ?? null,
        SaftTaxCode: row.SaftTaxCode ?? null,
        CategTvaMotivScutire: row.CategTvaMotivScutire ?? null,
        Custom: row.Custom ?? null,
        CustomId: row.CustomId ?? null,
        GarantieLuni: row.GarantieLuni ?? null,
        CodNc8: row.CodNc8 ?? null,
        MasaNet: row.MasaNet ?? null,
        MasaBrut: row.MasaBrut ?? null,
        StructIdEditabil: row.StructIdEditabil ?? null,
        UnitateAmbalajId: row.UnitateAmbalajId ?? null,
        CantitateFa: row.CantitateFa ?? null,
        CantitatePt1Fa: row.CantitatePt1Fa ?? null,
        CodLotFurnizor: row.CodLotFurnizor ?? null,
        DataExp: row.DataExp ?? null,
        CodLotIntern: row.CodLotIntern ?? null,
        LocDepozitare: row.LocDepozitare ?? null,
        TaxaUnitara: row.TaxaUnitara ?? null,
    };
}

// Cantitate=1 implicit pe rând nou — fără asta, un rând nesalvat rămâne cu Cantitate
// nesetată (undefined), iar la salvare `buildLiniePayload` cădea silențios pe 0, generând
// o linie fără valoare (bug confirmat 2026-07-29: userul vedea "1" în editor dar rândul
// nu avea de fapt nicio valoare setată, deci se salva Cantitate=0/Valoare=0).
function onInitNewRow(e: any) {
    e.data.Cantitate = 1;
}

async function reloadAfterSave() {
    const data = await dsLinii.value?.reload();
    emit('has-linii-changed', ((data as any[])?.length ?? 0) > 0);
    emit('refresh-document', props.document.Id);
}

async function onRowInserting(e: any) {
    const data = { ...e.data };
    e.cancel = true;
    setTimeout(() => gridRef.value?.instance?.cancelEditData(), 0);
    if (!data.ProdusId) {
        toast('Selectează produsul înainte de a salva linia.', 'error', 3000);
        return;
    }
    try {
        await DOC_Linii.save(props.document.Id, buildLiniePayload(data));
        await reloadAfterSave();
        toast('Linia a fost adăugată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

async function onRowUpdating(e: any) {
    const merged = { ...e.oldData, ...e.newData };
    e.cancel = true;
    setTimeout(() => gridRef.value?.instance?.cancelEditData(), 0);
    try {
        await DOC_Linii.update(e.key, buildLiniePayload(merged));
        await reloadAfterSave();
        toast('Linia a fost actualizată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

// ── Evidențiere vizuală linie modificată/nouă nesalvată ──
// cellPrepared (nu rowPrepared) — în batch mode DevExtreme repictează celula editată la
// fiecare modificare, dar nu re-declanșează sigur rowPrepared pentru tot rândul; cellPrepared
// e apelat pentru fiecare celulă a rândului afectat, deci colorează tot rândul corect.
function onCellPrepared(e: any) {
    if (e.rowType !== 'data') return;
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    const change = changes.find((c) => c.key === e.row?.key);

    // Linie nouă nesalvată — tot rândul e "nou", nu are sens să evidențiem doar un câmp.
    const isNewRow = change?.type === 'insert';
    // Linie existentă editată — evidențiem DOAR celula al cărei câmp a fost chiar modificat,
    // la fel ca `.attr-cell-dirty` din GridAtributeDinamice.vue (Personal-Salarizare).
    const fieldChanged = !isNewRow && !!change && !!e.column?.dataField
        && Object.prototype.hasOwnProperty.call(change.data, e.column.dataField);

    e.cellElement.classList.toggle('linie-noua', isNewRow);
    e.cellElement.classList.toggle('celula-modificata', fieldChanged);

    recomputeDirtyCount();
}

// ── Contor total de câmpuri modificate (afișat ca badge pe tab-ul documentului) ──
// O linie nouă nesalvată numără ca 1 unitate; o linie existentă editată numără câte
// câmpuri diferite are schimbate (Object.keys(change.data).length).
function recomputeDirtyCount() {
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as Array<{ type: string; data: any }>;
    let count = 0;
    for (const c of changes) {
        if (c.type === 'insert') count += 1;
        else if (c.type === 'update') count += Object.keys(c.data ?? {}).length;
    }
    emit('dirty-count-change', count);
}

// ── Revert per linie (rând modificat sau nou nesalvat), ca la Personal-Salarizare ──
function isRowDirty(cellInfo: any): boolean {
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as Array<{ key: any }>;
    return changes.some((c) => c.key === cellInfo.key);
}

function revertRow(cellInfo: any) {
    const instance = gridRef.value?.instance;
    if (!instance) return;

    const changes = (instance.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    const idx = changes.findIndex((c) => c.key === cellInfo.key);
    if (idx === -1) return;

    if (changes[idx].type === 'insert') {
        // Rând nou, nesalvat încă — se elimină complet din grid.
        instance.deleteRow(cellInfo.rowIndex);
    } else {
        // Editare pe rând existent — se șterge doar modificarea, revine la valorile din server.
        instance.option('editing.changes', changes.filter((_, i) => i !== idx));
    }
    recomputeDirtyCount();
}

// ── Revert per câmp — buton "revert" injectat direct în editor, la fel ca în
// Personal/GeneralTab.vue (markEditor: editor.option('buttons', [{name:'revertInitial', ...}])).
// Apare doar când câmpul respectiv chiar diferă de valoarea originală din server.
function currentEditingChange(rowKey: any) {
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    return changes.find((c) => c.key === rowKey);
}

function applyFieldRevertButton(editorInstance: any, dataField: string, rowKey: any, originalValue: any) {
    if (!editorInstance?.option) return;
    const change = currentEditingChange(rowKey);
    const isDirty = !!change && change.type === 'update' && Object.prototype.hasOwnProperty.call(change.data, dataField);

    const current = (editorInstance.option('buttons') ?? []) as Array<{ name?: string }>;
    const defaults = current.filter((b) => b?.name !== EDITOR_REVERT_BUTTON_NAME);
    const hasRevert = current.some((b) => b?.name === EDITOR_REVERT_BUTTON_NAME);

    if (isDirty && !hasRevert) {
        editorInstance.option('buttons', [
            {
                name: EDITOR_REVERT_BUTTON_NAME,
                location: 'before',
                options: {
                    icon: 'revert',
                    stylingMode: 'text',
                    hint: 'Revenire la valoarea inițială',
                    onClick: () => revertField(rowKey, dataField, originalValue, editorInstance),
                },
            },
            ...defaults,
        ]);
    } else if (!isDirty && hasRevert) {
        editorInstance.option('buttons', defaults);
    }
}

function revertField(rowKey: any, dataField: string, originalValue: any, editorInstance: any) {
    const instance = gridRef.value?.instance;
    if (!instance) return;

    editorInstance?.option?.('value', originalValue);

    const changes = (instance.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    const idx = changes.findIndex((c) => c.key === rowKey);
    if (idx === -1) return;

    const newData = { ...changes[idx].data };
    delete newData[dataField];

    if (Object.keys(newData).length === 0) {
        instance.option('editing.changes', changes.filter((_, i) => i !== idx));
    } else {
        const newChanges = [...changes];
        newChanges[idx] = { ...changes[idx], data: newData };
        instance.option('editing.changes', newChanges);
    }
    recomputeDirtyCount();
}

function onEditorPreparing(e: any) {
    if (e.parentType !== 'dataRow' || !e.dataField) return;

    const dataField = e.dataField;
    const rowKey = e.row?.key;
    const originalValue = e.row?.data?.[dataField];

    const defaultValueChanged = e.editorOptions.onValueChanged;
    e.editorOptions.onValueChanged = function (args: any) {
        defaultValueChanged?.(args);
        applyFieldRevertButton(args.component, dataField, rowKey, originalValue);
    };
}

async function onSalveazaTot() {
    if (isSalvandTot.value) return;
    const instance = gridRef.value?.instance;
    if (!instance) return;

    const changes = (instance.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    if (changes.length === 0) {
        instance.cancelEditData();
        return;
    }

    isSalvandTot.value = true;
    let errors = 0;
    try {
        for (const change of changes) {
            try {
                if (change.type === 'insert') {
                    if (!change.data.ProdusId) throw new Error('Selectează produsul pentru linia nouă.');
                    await DOC_Linii.save(props.document.Id, buildLiniePayload(change.data));
                } else if (change.type === 'update') {
                    const rowData = instance.getVisibleRows().find((r: any) => r.key === change.key)?.data ?? {};
                    await DOC_Linii.update(change.key, buildLiniePayload({ ...rowData, ...change.data }));
                }
            } catch (err: any) {
                errors++;
                toast(getErrMsg(err), 'error', 3000);
            }
        }
        instance.cancelEditData();
        await reloadAfterSave();
        if (errors === 0) toast('Modificările au fost salvate.', 'success', 1500);
    } finally {
        isSalvandTot.value = false;
    }
}

// ── Enter în grid → "Salvează tot", cu excepția dropdown-ului de produs deschis ──
function onGridKeyDown(e: any) {
    if (e.event?.key !== 'Enter') return;
    if (currentDropBoxInstance?.option('opened')) return;
    setTimeout(() => {
        if (currentDropBoxInstance?.option('opened')) return;
        onSalveazaTot();
    }, 0);
}

// ── Auto-save la focusout din grid (pattern DevExtreme batch mode, portat din Comenzi) ──
function onGridFocusOut() {
    if (isSalvandTot.value) return;
    const instance = gridRef.value?.instance;
    if (!instance) return;

    setTimeout(() => {
        const changes = (instance.option('editing.changes') ?? []) as any[];
        if (changes.length === 0) return;

        const active = window.document.activeElement as HTMLElement | null;
        if (active?.closest('.linii-documente-grid')) return;
        if (active?.closest('.dx-overlay-wrapper')) return;
        onSalveazaTot();
    }, 150);
}

function onGridInitialized(e: any) {
    (e.component.element() as HTMLElement).addEventListener('focusout', onGridFocusOut);
}

onMounted(() => {
    loadDataSources();
    fetchTipDocumentInfo();
});

onUnmounted(() => {
    (gridRef.value?.instance?.element() as HTMLElement | undefined)?.removeEventListener('focusout', onGridFocusOut);
});

__expose({ reload: reloadAfterSave });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(DxDataGrid), {
      ref_key: "gridRef",
      ref: gridRef,
      class: "linii-documente-grid",
      height: "100%",
      "data-source": dsLinii.value,
      "key-expr": "Id",
      "show-borders": "",
      "column-auto-width": false,
      "column-min-width": 70,
      "allow-column-resizing": true,
      "column-resizing-mode": "nextColumn",
      "allow-column-reordering": true,
      "focused-row-enabled": true,
      "show-column-lines": true,
      "show-row-lines": true,
      "row-alternation-enabled": true,
      "hover-state-enabled": true,
      "column-chooser": { enabled: true, mode: 'select', allowSearch: true },
      "filter-row": { visible: true },
      paging: { enabled: false },
      pager: { visible: false },
      onRowInserting: onRowInserting,
      onRowUpdating: onRowUpdating,
      onInitNewRow: onInitNewRow,
      onKeyDown: onGridKeyDown,
      onInitialized: onGridInitialized,
      onCellPrepared: onCellPrepared,
      onEditorPreparing: onEditorPreparing
    }, {
      deblocheazaBtn: _withCtx(() => [
        _createVNode(_unref(DxButton), {
          text: isUnlocked.value ? 'Blochează' : 'Deblochează',
          icon: isUnlocked.value ? 'unlock' : 'lock',
          type: isUnlocked.value ? 'normal' : 'default',
          "styling-mode": "outlined",
          onClick: onToggleLock
        }, null, 8, ["text", "icon", "type"])
      ]),
      adaugaLinieBtn: _withCtx(() => [
        _createVNode(_unref(DxButton), {
          text: "Adaugă linie",
          icon: "plus",
          type: "default",
          "styling-mode": "outlined",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (gridRef.value?.instance?.addRow()))
        })
      ]),
      importExcelBtn: _withCtx(() => [
        _createVNode(_unref(DxButton), {
          text: "Import linii din Excel",
          icon: "xlsxfile",
          type: "default",
          "styling-mode": "outlined",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (showImportExcelPopup.value = true))
        })
      ]),
      salveazaTotBtn: _withCtx(() => [
        _createVNode(_unref(DxButton), {
          text: "Salvează tot",
          icon: "save",
          type: "success",
          "styling-mode": "contained",
          disabled: isSalvandTot.value,
          onClick: onSalveazaTot
        }, null, 8, ["disabled"])
      ]),
      eticheteBtn: _withCtx(() => [
        _createVNode(_unref(DxButton), {
          text: "Etichete",
          icon: "tags",
          type: "default",
          "styling-mode": "outlined",
          onClick: onOpenEticheteDocument
        })
      ]),
      produsEditor: _withCtx(({ data: cellInfo }) => [
        _createVNode(_unref(DxDropDownBox), {
          value: cellInfo.value,
          "data-source": dsProdus.value,
          "display-expr": produsDisplayExpr,
          "value-expr": "Id",
          "show-clear-button": true,
          "drop-down-options": { width: 700, height: 450 },
          onOpened: onDropBoxOpened,
          onClosed: onDropBoxClosed,
          onValueChanged: (e) => { if (e.value == null) cellInfo.setValue(null); },
          onKeyDown: (e) => onProdusBoxKeyDown(e, cellInfo)
        }, {
          content: _withCtx(() => [
            _createVNode(_unref(DxDataGrid), {
              ref_key: "innerProdusGridRef",
              ref: innerProdusGridRef,
              "data-source": dsProdus.value,
              selection: { mode: 'single' },
              "focused-row-enabled": true,
              "hover-state-enabled": true,
              "selected-row-keys": cellInfo.value ? [cellInfo.value] : [],
              height: 410,
              "show-borders": false,
              "show-column-lines": false,
              "show-row-lines": true,
              "filter-row": { visible: true },
              paging: { pageSize: 25 },
              "key-expr": "Id",
              onContentReady: onProdusGridContentReady,
              onSelectionChanged: (e) => onProdusGridSelection(e, cellInfo),
              onKeyDown: (e) => onProdusGridKeyDown(e, cellInfo)
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DxColumn), {
                  "data-field": "Cod",
                  caption: "Cod",
                  width: 110
                }),
                _createVNode(_unref(DxColumn), {
                  "data-field": "CodIntern",
                  caption: "Cod Intern",
                  width: 130
                }),
                _createVNode(_unref(DxColumn), {
                  "data-field": "Nume",
                  caption: "Denumire"
                })
              ]),
              _: 1
            }, 8, ["data-source", "selected-row-keys", "onSelectionChanged", "onKeyDown"])
          ]),
          _: 2
        }, 1032, ["value", "data-source", "onValueChanged", "onKeyDown"])
      ]),
      revertCellTemplate: _withCtx(({ data }) => [
        (isRowDirty(data))
          ? (_openBlock(), _createBlock(_unref(DxButton), {
              key: 0,
              icon: "revert",
              "styling-mode": "text",
              hint: "Revenire la valoarea inițială",
              onClick: ($event: any) => (revertRow(data))
            }, null, 8, ["onClick"]))
          : _createCommentVNode("", true)
      ]),
      eticheteNumarCellTemplate: _withCtx(({ data }) => [
        _createElementVNode("span", _hoisted_2, [
          _createVNode(_unref(DxButton), {
            icon: "tags",
            "styling-mode": "text",
            hint: "Etichete linie",
            onClick: ($event: any) => (onOpenEticheteLinie(data.data))
          }, null, 8, ["onClick"]),
          (data.data.EticheteNumar)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(data.data.EticheteNumar), 1))
            : _createCommentVNode("", true)
        ])
      ]),
      departamentEditor: _withCtx(({ data: cellInfo }) => [
        _createVNode(_unref(DxDropDownBox), {
          value: cellInfo.value,
          "data-source": structuraCompanieItems.value,
          "value-expr": "Id",
          "display-expr": departamentDisplayExpr,
          "show-clear-button": true,
          onValueChanged: (e) => cellInfo.setValue(e.value ?? null)
        }, {
          content: _withCtx(() => [
            _createVNode(_unref(DxTreeView), {
              items: structuraCompanieTree.value,
              "key-expr": "Id",
              "display-expr": "Nume",
              "selection-mode": "single",
              "select-by-click": true,
              onItemClick: (e) => cellInfo.setValue(e.itemData?.Id ?? null)
            }, null, 8, ["items", "onItemClick"])
          ]),
          _: 2
        }, 1032, ["value", "data-source", "onValueChanged"])
      ]),
      default: _withCtx(() => [
        _createVNode(_unref(DxStateStoring), {
          enabled: true,
          type: "localStorage",
          "storage-key": stateStoringKey.value
        }, null, 8, ["storage-key"]),
        _createVNode(_unref(DxSelection), { mode: "single" }),
        _createVNode(_unref(DxEditing), {
          mode: "batch",
          "allow-updating": isUnlocked.value,
          "allow-adding": isUnlocked.value,
          "allow-deleting": false,
          "use-icons": true,
          "new-row-position": "last"
        }, null, 8, ["allow-updating", "allow-adding"]),
        _createVNode(_unref(DxExport), {
          enabled: true,
          formats: ['pdf', 'xlsx']
        }),
        _createVNode(_unref(DxToolbar), null, {
          default: _withCtx(() => [
            _createVNode(_unref(DxItem), {
              location: "before",
              template: "deblocheazaBtn"
            }),
            (isUnlocked.value)
              ? (_openBlock(), _createBlock(_unref(DxItem), {
                  key: 0,
                  location: "before",
                  template: "adaugaLinieBtn"
                }))
              : _createCommentVNode("", true),
            (isUnlocked.value)
              ? (_openBlock(), _createBlock(_unref(DxItem), {
                  key: 1,
                  location: "before",
                  template: "importExcelBtn"
                }))
              : _createCommentVNode("", true),
            (isUnlocked.value)
              ? (_openBlock(), _createBlock(_unref(DxItem), {
                  key: 2,
                  location: "before",
                  template: "salveazaTotBtn"
                }))
              : _createCommentVNode("", true),
            (isUnlocked.value && areEtichete.value)
              ? (_openBlock(), _createBlock(_unref(DxItem), {
                  key: 3,
                  location: "before",
                  template: "eticheteBtn"
                }))
              : _createCommentVNode("", true),
            _createVNode(_unref(DxItem), { name: "columnChooserButton" }),
            _createVNode(_unref(DxItem), { name: "exportButton" })
          ]),
          _: 1
        }),
        _createVNode(_unref(DxColumn), {
          width: 40,
          caption: "",
          "allow-editing": false,
          "allow-sorting": false,
          "allow-filtering": false,
          fixed: true,
          "fixed-position": "left",
          "cell-template": "revertCellTemplate"
        }),
        _createVNode(_unref(DxColumn), {
          "data-field": "ProdusId",
          caption: "Produs",
          "min-width": 200,
          "calculate-display-value": getProdusNume,
          "edit-cell-template": "produsEditor"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxRequiredRule), { message: "Produsul este obligatoriu." })
          ]),
          _: 1
        }),
        _createVNode(_unref(DxColumn), {
          "data-field": "Produs.Cod",
          caption: "Cod",
          width: "110",
          "allow-editing": false,
          visible: colVisible('Cod')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "Produs.CodIntern",
          caption: "Cod Intern",
          width: "110",
          "allow-editing": false,
          visible: colVisible('CodIntern')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "Nume",
          caption: "Denumire Alternativă",
          visible: colVisible('Nume')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "Cantitate",
          caption: "Cantitate",
          "data-type": "number",
          format: "#,##0.00",
          width: "100",
          visible: colVisible('Cantitate')
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxRequiredRule), { message: "Cantitatea este obligatorie." }),
            _createVNode(_unref(DxRangeRule), {
              min: 0.0001,
              message: "Cantitatea trebuie să fie mai mare decât 0."
            })
          ]),
          _: 1
        }, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "UnitateId",
          caption: "UM",
          width: "90",
          visible: colVisible('Unitate')
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxLookup), {
              "data-source": dsUnitate.value ?? [],
              "display-expr": "Simbol",
              "value-expr": "Id"
            }, null, 8, ["data-source"])
          ]),
          _: 1
        }, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "PretUnitar",
          caption: "Preț Unitar",
          "data-type": "number",
          format: "#,##0.0000",
          width: "110",
          visible: colVisible('PretUnitar')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "Valoare",
          caption: "Valoare",
          "data-type": "number",
          format: "#,##0.00",
          width: "110",
          "allow-editing": false,
          visible: colVisible('Valoare')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "AdaosUnitar",
          caption: "Adaos/Discount Unitar",
          "data-type": "number",
          format: "#,##0.0000",
          width: "140",
          visible: colVisible('AdaosUnitar')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "Adaos",
          caption: "Adaos/Discount %",
          "data-type": "number",
          format: "## '%'",
          width: "110",
          visible: colVisible('Adaos')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "PretCuAdaos",
          caption: "Preț cu Adaos/Discount",
          "data-type": "number",
          format: "#,##0.0000",
          width: "140",
          "allow-editing": false,
          visible: colVisible('PretCuAdaos')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "ValoareAdaos",
          caption: "Valoare Adaos/Discount",
          "data-type": "number",
          format: "#,##0.00",
          width: "140",
          visible: colVisible('ValoareAdaos')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "ValoareCuAdaos",
          caption: "Valoare cu Adaos/Discount",
          "data-type": "number",
          format: "#,##0.00",
          width: "150",
          "allow-editing": false,
          visible: colVisible('ValoareCuAdaos')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "Tva",
          caption: "TVA %",
          "data-type": "number",
          format: "## '%'",
          width: "90",
          visible: colVisible('Tva')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "ValoareTva",
          caption: "Valoare TVA",
          "data-type": "number",
          format: "#,##0.00",
          width: "110",
          "allow-editing": false,
          visible: colVisible('ValoareTva')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "Total",
          caption: "Total",
          "data-type": "number",
          format: "#,##0.00",
          width: "110",
          "allow-editing": false,
          visible: colVisible('Total')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          caption: "Total RON",
          "calculate-cell-value": calculateTotalRon,
          "data-type": "number",
          format: "#,##0.00",
          width: "110",
          "allow-editing": false,
          visible: colVisible('TotalRon')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CategorieContabilaId",
          caption: "Categorie Contabilă",
          width: "160",
          visible: colVisible('CategorieContabila')
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxLookup), {
              "data-source": dsCategorieContabila.value ?? [],
              "display-expr": "Nume",
              "value-expr": "Id"
            }, null, 8, ["data-source"])
          ]),
          _: 1
        }, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "DebitId",
          caption: "Debit",
          width: "130",
          visible: colVisible('Debit')
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxLookup), {
              "data-source": dsConturi.value ?? [],
              "display-expr": contDisplayExpr,
              "value-expr": "Id"
            }, null, 8, ["data-source"])
          ]),
          _: 1
        }, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CreditId",
          caption: "Credit",
          width: "130",
          visible: colVisible('Credit')
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxLookup), {
              "data-source": dsConturi.value ?? [],
              "display-expr": contDisplayExpr,
              "value-expr": "Id"
            }, null, 8, ["data-source"])
          ]),
          _: 1
        }, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CategTva",
          caption: "Categorie TVA",
          width: "120",
          visible: colVisible('CategTva')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "SaftTaxCode",
          caption: "Cod TVA (SAF-T)",
          width: "130",
          visible: colVisible('SaftTaxCode')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CategTvaMotivScutire",
          caption: "Categorie TVA Motiv Scutire",
          width: "180",
          visible: colVisible('CategTvaMotivScutire')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "Custom",
          caption: "Explicație",
          width: "160",
          visible: colVisible('Custom')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CustomId",
          caption: "Scop Operațional",
          width: "160",
          visible: colVisible('CustomList')
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxLookup), {
              "data-source": dsCustomList.value ?? [],
              "display-expr": "Nume",
              "value-expr": "Id"
            }, null, 8, ["data-source"])
          ]),
          _: 1
        }, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "GarantieLuni",
          caption: "Garanție (luni)",
          "data-type": "number",
          width: "110",
          visible: colVisible('GarantieLuni')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "LotId",
          caption: "Lot ID",
          width: "110",
          "allow-editing": false,
          visible: colVisible('LotId')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CodNc8",
          caption: "Cod NC8",
          width: "110",
          visible: colVisible('CodNc8')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "MasaNet",
          caption: "Masa Net",
          "data-type": "number",
          format: "#,##0.0000",
          width: "110",
          visible: colVisible('MasaNet')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "MasaBrut",
          caption: "Masa Brut",
          "data-type": "number",
          format: "#,##0.0000",
          width: "110",
          visible: colVisible('MasaBrut')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "StructIdEditabil",
          caption: "Departament",
          width: "200",
          "calculate-display-value": getDepartamentDisplay,
          "edit-cell-template": "departamentEditor",
          visible: colVisible('Departament')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "LocDepozitare",
          caption: "Loc Depozitare",
          width: "130",
          visible: colVisible('LocDepozitare')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "UnitateAmbalajId",
          caption: "UM FA",
          width: "90",
          visible: colVisible('UnitateAmbalaj')
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxLookup), {
              "data-source": dsUnitate.value ?? [],
              "display-expr": "Simbol",
              "value-expr": "Id"
            }, null, 8, ["data-source"])
          ]),
          _: 1
        }, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CantitateFa",
          caption: "Etichete",
          "data-type": "number",
          format: "#,##0.00",
          width: "100",
          visible: colVisible('CantitateFa')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CantitatePt1Fa",
          caption: "Cantitate pentru 1 FA",
          "data-type": "number",
          format: "#,##0.00",
          width: "150",
          visible: colVisible('CantitatePt1Fa')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CodLotFurnizor",
          caption: "Lot Furnizor",
          width: "120",
          visible: colVisible('CodLotFurnizor')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "CodLotIntern",
          caption: "Lot Intern",
          width: "120",
          visible: colVisible('CodLotIntern')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "DataExp",
          caption: "Data Expirării",
          "data-type": "date",
          format: "dd.MM.yyyy",
          width: "120",
          visible: colVisible('DataExp')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          "data-field": "TaxaUnitara",
          caption: "Taxă Unitară",
          "data-type": "number",
          format: "#,##0.0000",
          width: "110",
          visible: colVisible('TaxaUnitara')
        }, null, 8, ["visible"]),
        _createVNode(_unref(DxColumn), {
          caption: "Nr. Etichete",
          width: "100",
          "allow-editing": false,
          "allow-sorting": false,
          "allow-filtering": false,
          visible: areEtichete.value,
          "cell-template": "eticheteNumarCellTemplate"
        }, null, 8, ["visible"])
      ]),
      _: 1
    }, 8, ["data-source"]),
    _createVNode(PopupImportLiniiExcel, {
      visible: showImportExcelPopup.value,
      "onUpdate:visible": _cache[2] || (_cache[2] = ($event: any) => ((showImportExcelPopup).value = $event)),
      document: props.document,
      onLiniiAdded: reloadAfterSave
    }, null, 8, ["visible", "document"])
  ]))
}
}

})